import React, { Suspense, lazy, useEffect, useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  ArrowLeft,
  BookOpen,
  GraduationCap,
  Info,
  Play,
  Plus,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Touchable, PrimaryButton, SoftButton, IconNavButton, SuccessButton } from './touchable';
import { getFullState, updateSession, deleteSession, archiveSession, savePattern } from '../storage/persistence';
import { calculateIRState } from '../logic/engine.ir';
import { resolveSessionPattern } from '../logic/sessionPattern';
import { deriveColor, resolvePatternColor } from '../logic/patternColor';
import { generateDsl } from '../logic/dsl-generator';
import { Logo } from './Logo';
import { SessionCard, SessionCardGrid, type DisplaySession } from './SessionCard';
import { PatternParamsCard } from './PatternParamsCard';
import { ProjectDetailsForm } from './ProjectDetailsForm';
import { defaultsForAll } from './paramHelpers';
import type { Session } from '../types/session';
import type { Pattern } from '../types/state';
import type { IRValue } from '../types/ir';

// Modals open on user interaction — never visible on first paint. Lazy
// imports keep their `@headlessui/react` Dialog/Transition machinery
// (~28 KB gz combined) out of the eager bundle.
const SessionActionModal = lazy(() => import('./SessionActionModal'));
const RenameProjectPrompt = lazy(() =>
  import('./RenameProjectPrompt').then((m) => ({ default: m.RenameProjectPrompt })),
);

interface SessionDashboardProps {
  onSelectSession: (sessionId: string) => void;
  onStartNewProject: () => void;
  onJustKnit: () => void;
  onViewLibrary: () => void;
  onSeeAll: () => void;
}

export const SessionDashboard: React.FC<SessionDashboardProps> = ({
  onSelectSession,
  onStartNewProject,
  onJustKnit,
  onViewLibrary,
  onSeeAll,
}) => {
  const { t } = useTranslation('session');
  const [sessions, setSessions] = useState<Record<string, Session>>({});
  const [patterns, setPatterns] = useState<Record<string, Pattern>>({});
  const [loading, setLoading] = useState(true);
  const [actionTarget, setActionTarget] = useState<DisplaySession | null>(null);
  const [busy, setBusy] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DisplaySession | null>(null);

  useEffect(() => {
    async function loadData() {
      const state = await getFullState();
      setSessions(state.sessions);
      setPatterns(state.patterns);
      setLoading(false);
    }
    loadData();
  }, []);

  const sessionList = useMemo<DisplaySession[]>(() => {
    return Object.values(sessions)
      .map((session) => {
        const pattern = patterns[session.patternId];
        if (!pattern) return null;
        const ir = resolveSessionPattern(pattern, session);
        if (!ir) return null;
        if (session.status === 'archived') return null;

        const derived = calculateIRState(ir, session.history);
        const currentRow = derived.currentRow;
        const totalRows = derived.totalRows;
        const progress = totalRows > 0 ? (currentRow / totalRows) * 100 : 0;

        return {
          id: session.id,
          title: session.title || pattern.title,
          progress,
          currentRow,
          totalRows,
          updatedAt: new Date(session.updatedAt),
          archived: false,
          color: resolvePatternColor(pattern),
          ...(session.metadata ? { metadata: session.metadata } : {}),
        };
      })
      .filter((s): s is DisplaySession => s !== null)
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, 6);
  }, [sessions, patterns]);

  const archivedCount = useMemo(
    () => Object.values(sessions).filter((s) => s.status === 'archived').length,
    [sessions],
  );

  const activeCount = useMemo(
    () => Object.values(sessions).filter((s) => s.status !== 'archived').length,
    [sessions],
  );

  const reload = async () => {
    const state = await getFullState();
    setSessions(state.sessions);
    setPatterns(state.patterns);
  };

  const handleDelete = async () => {
    if (!actionTarget) return;
    setBusy(true);
    await deleteSession(actionTarget.id);
    setActionTarget(null);
    await reload();
    setBusy(false);
  };

  const handleArchive = async () => {
    if (!actionTarget) return;
    setBusy(true);
    await archiveSession(actionTarget.id);
    setActionTarget(null);
    await reload();
    setBusy(false);
  };

  const handleRenameStart = () => {
    if (!actionTarget) return;
    setRenameTarget(actionTarget);
    setActionTarget(null);
  };

  const handleRenameConfirm = async (newTitle: string) => {
    if (!renameTarget) return;
    const session = sessions[renameTarget.id];
    if (!session) {
      setRenameTarget(null);
      return;
    }
    setBusy(true);
    await updateSession({ ...session, title: newTitle });
    setRenameTarget(null);
    await reload();
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-ink-faint">
        {t('dashboard.loading', 'Loading your projects...')}
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col overflow-hidden">
      {/* Landscape (`short:`) on phone collapses padding, hides the subtitle,
          shrinks the brand and nav, and trims the chunky action buttons so the
          whole dashboard fits in a 390px-tall viewport without scrolling
          past the bottom action row. */}
      <main className="w-full max-w-md md:max-w-5xl mx-auto p-6 short:p-3 flex flex-col flex-1 min-h-0 gap-6 short:gap-2">
        <header className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <h1 className="m-0">
              <Logo className="text-2xl sm:text-3xl short:text-xl" />
            </h1>
            <p className="text-xs sm:text-sm text-ink-faint mt-0.5 short:hidden">
              {t('dashboard.tagline', 'Row counter & timer for knitters')}
            </p>
          </div>
          <div className="flex items-center gap-2 short:gap-1 shrink-0">
            <Link
              to="/learn/"
              aria-label={t('dashboard.nav.learn', 'Learn')}
              title={t('dashboard.nav.learn', 'Learn')}
              className="w-10 h-10 short:w-8 short:h-8 bg-soft rounded-full flex items-center justify-center hover:bg-line transition-colors"
            >
              <GraduationCap className="w-5 h-5 short:w-4 short:h-4 text-ink-faint" />
            </Link>
            <Link
              to="/about/"
              aria-label={t('dashboard.nav.about', 'About')}
              title={t('dashboard.nav.about', 'About')}
              className="w-10 h-10 short:w-8 short:h-8 bg-soft rounded-full flex items-center justify-center hover:bg-line transition-colors"
            >
              <Info className="w-5 h-5 short:w-4 short:h-4 text-ink-faint" />
            </Link>
            <Link
              to="/settings/"
              aria-label={t('dashboard.nav.settings', 'Settings')}
              title={t('dashboard.nav.settings', 'Settings')}
              className="w-10 h-10 short:w-8 short:h-8 bg-soft rounded-full flex items-center justify-center hover:bg-line transition-colors"
            >
              <SettingsIcon className="w-5 h-5 short:w-4 short:h-4 text-ink-faint" />
            </Link>
          </div>
        </header>

        <section className="flex-1 min-h-0 flex flex-col">
          <div className="flex justify-between items-end mb-4 short:mb-1.5 shrink-0">
            <h2 className="text-lg short:text-xs short:uppercase short:tracking-widest font-bold text-ink short:text-ink-faint">
              {t('dashboard.recentProjects', 'Recent Projects')}
            </h2>
          </div>

          {sessionList.length > 0 ? (
            <div className="relative flex-1 min-h-0 overflow-hidden">
              {/* Portrait: single column below md, responsive grid at md+.
                  Landscape phone (`short:`) forces a 2-column grid even
                  though phone-landscape width (~844px) qualifies as md —
                  the responsive grid already gives us 2 columns there.
                  We bias card density harder in landscape via tighter gap
                  and card padding (handled in SessionCard). */}
              <div className="md:hidden">
                <SessionCardGrid columns="single">
                  {sessionList.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      onSelect={onSelectSession}
                      onManage={setActionTarget}
                    />
                  ))}
                </SessionCardGrid>
              </div>
              <div className="hidden md:block">
                <SessionCardGrid columns="responsive">
                  {sessionList.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      onSelect={onSelectSession}
                      onManage={setActionTarget}
                    />
                  ))}
                </SessionCardGrid>
              </div>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-16 short:h-6 bg-gradient-to-t from-base to-transparent"
              />
            </div>
          ) : (
            <div className="bg-base border-2 border-dashed border-line rounded-3xl short:rounded-2xl p-10 short:p-4 text-center shrink-0">
              <p className="text-ink-faint font-medium mb-2 short:text-sm short:mb-1">
                {t('dashboard.empty.headline', 'No active projects yet.')}
              </p>
              <p className="text-sm short:text-xs text-ink-faint">
                <Trans
                  i18nKey="dashboard.empty.cta"
                  ns="session"
                  defaults="New to Knit in Time? <gs>Walk through Getting Started →</gs>"
                  components={{
                    gs: (
                      <Link
                        to="/learn/getting-started/"
                        className="text-primary font-bold underline"
                      />
                    ),
                  }}
                />
              </p>
            </div>
          )}

          {(activeCount > 3 || archivedCount > 0) && (
            <div className="mt-3 short:mt-1 text-right shrink-0">
              <Touchable
                haptic="softTap"
                onClick={onSeeAll}
                className="text-sm short:text-xs font-bold text-primary hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                {t('dashboard.seeAll', 'See all →')}
              </Touchable>
            </div>
          )}
        </section>

        <section className="space-y-3 short:space-y-2">
          <PrimaryButton
            onClick={onStartNewProject}
            className="py-6 short:py-2.5 rounded-3xl short:rounded-2xl font-black text-xl short:text-base shadow-lg dark:shadow-none active:scale-95 flex items-center justify-center gap-3 short:gap-2"
          >
            <Plus className="w-6 h-6 short:w-5 short:h-5" strokeWidth={3} />
            {t('dashboard.actions.newProject', 'NEW PROJECT')}
          </PrimaryButton>
          <div className="grid grid-cols-2 gap-3 short:gap-2">
            <SuccessButton
              onClick={onJustKnit}
              className="py-5 short:py-2 rounded-3xl short:rounded-2xl font-extrabold text-lg short:text-sm shadow-lg dark:shadow-none active:scale-95 flex items-center justify-center gap-2 short:gap-1.5"
            >
              <Play className="w-5 h-5 short:w-4 short:h-4" fill="currentColor" />
              {t('dashboard.actions.justKnit', 'JUST KNIT')}
            </SuccessButton>
            <SoftButton
              onClick={onViewLibrary}
              className="py-5 short:py-2 text-ink rounded-3xl short:rounded-2xl font-extrabold text-lg short:text-sm shadow-sm active:scale-95 flex items-center justify-center gap-2 short:gap-1.5"
            >
              <BookOpen className="w-5 h-5 short:w-4 short:h-4" />
              {t('dashboard.actions.library', 'LIBRARY')}
            </SoftButton>
          </div>
        </section>
      </main>

      {actionTarget !== null && (
        <Suspense fallback={null}>
          <SessionActionModal
            target={actionTarget}
            busy={busy}
            onClose={() => setActionTarget(null)}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onRename={handleRenameStart}
          />
        </Suspense>
      )}

      {renameTarget && (
        <Suspense fallback={null}>
          <RenameProjectPrompt
            currentTitle={renameTarget.title}
            onCancel={() => setRenameTarget(null)}
            onConfirm={handleRenameConfirm}
          />
        </Suspense>
      )}
    </div>
  );
};

interface NewProjectWizardProps {
  onCancel: () => void;
  onProjectCreated: (sessionId: string) => void;
  initialPattern?: Pattern;
}

export const NewProjectWizard: React.FC<NewProjectWizardProps> = ({
  onCancel,
  onProjectCreated,
  initialPattern,
}) => {
  const { t } = useTranslation('session');
  const [patterns, setPatterns] = useState<Record<string, Pattern>>({});
  const [loading, setLoading] = useState(!initialPattern);
  const [selected, setSelected] = useState<Pattern | null>(initialPattern ?? null);
  const [details, setDetails] = useState<{
    title: string;
    metadata: Record<string, string>;
    color: string;
  }>({
    title: initialPattern?.title ?? '',
    metadata: initialPattern?.structure?.metadata ? { ...initialPattern.structure.metadata } : {},
    color: '',
  });
  const [detailsValid, setDetailsValid] = useState(true);
  const [paramValues, setParamValues] = useState<Record<string, IRValue>>(
    initialPattern ? defaultsForAll(initialPattern.structure?.params ?? []) : {},
  );
  const [paramsValid, setParamsValid] = useState(true);

  useEffect(() => {
    if (initialPattern) return;
    async function loadPatterns() {
      const state = await getFullState();
      setPatterns(state.patterns);
      setLoading(false);
    }
    loadPatterns();
  }, [initialPattern]);

  const handleStart = async () => {
    if (!selected || !detailsValid || !paramsValid) return;
    const sessionId = crypto.randomUUID();
    const now = new Date().toISOString();
    const params = selected.structure?.params ?? [];
    let compiledDsl: string | undefined;
    if (selected.js_source) {
      const defaults = defaultsForAll(selected.structure?.params ?? []);
      const isNonDefault = Object.keys(paramValues).some((k) => paramValues[k] !== defaults[k]);
      if (isNonDefault) {
        try {
          // Dynamic import keeps the JS pattern compiler (~3 KB gz) out of
          // the eager bundle. Only loaded when a user actually starts a
          // project from a JS-authored pattern with non-default params.
          const { executeJSPattern } = await import('../logic/js-authoring');
          const recompiledIR = await executeJSPattern(selected.js_source, paramValues);
          compiledDsl = generateDsl(recompiledIR);
        } catch (err) {
          alert(
            t(
              'newProject.errors.compileFailed',
              'Failed to compile pattern with chosen parameters: {{message}}',
              {
                message: err instanceof Error ? err.message : String(err),
              },
            ),
          );
          return;
        }
      }
    }
    const newSession: Session = {
      id: sessionId,
      patternId: selected.id,
      title: details.title.trim(),
      status: 'idle',
      startedAt: now,
      updatedAt: now,
      snapshot: { lastEventIndex: -1, currentRow: 0, totalTimeMs: 0 },
      history: [],
      ...(params.length > 0 ? { paramValues } : {}),
      ...(compiledDsl ? { compiledDsl } : {}),
      ...(Object.keys(details.metadata).length > 0 ? { metadata: details.metadata } : {}),
    };
    // Only persist when the user picked a color that differs from the pattern's
    // effective color (explicit `selected.color` if set, else the derived default).
    // Without this guard, every first project start would write the derived color
    // back to the pattern even when the user never touched the picker.
    const effectiveExisting = selected.color ?? deriveColor(selected.id);
    if (details.color && details.color !== effectiveExisting) {
      await savePattern({ ...selected, color: details.color });
    }
    await updateSession(newSession);
    onProjectCreated(sessionId);
  };

  const patternList = Object.values(patterns).filter((p) => !p.builtin);

  return (
    <div className="fixed inset-0 bg-base z-50 flex flex-col transition-colors duration-300">
      <header className="p-6 bg-card border-b border-line sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <IconNavButton
            onClick={selected ? (initialPattern ? onCancel : () => setSelected(null)) : onCancel}
            aria-label={t('common:actions.back', 'Back')}
            className="-ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </IconNavButton>
          <h2 className="text-2xl font-black font-display text-ink tracking-tight">
            {selected
              ? t('newProject.nameYourProject', 'Name your project')
              : t('newProject.selectPattern', 'Select Pattern')}
          </h2>
        </div>
      </header>

      {!selected ? (
        <div className="flex-grow overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-ink-faint py-10">
              {t('newProject.loadingLibrary', 'Loading library...')}
            </div>
          ) : patternList.length > 0 ? (
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {patternList.map((pattern) => (
                <Touchable
                  haptic="softTap"
                  key={pattern.id}
                  onClick={() => {
                    setSelected(pattern);
                    setDetails({
                      title: pattern.title,
                      metadata: pattern.structure?.metadata
                        ? { ...pattern.structure.metadata }
                        : {},
                      color: '',
                    });
                    setDetailsValid(true);
                    setParamValues(defaultsForAll(pattern.structure?.params ?? []));
                    setParamsValid(true);
                  }}
                  className="w-full text-left bg-card p-6 rounded-3xl border-2 border-transparent hover:border-line active:border-primary active:bg-primary-soft transition-all"
                >
                  <h3 className="font-bold text-ink text-lg mb-1">{pattern.title}</h3>
                </Touchable>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-ink-faint mb-6">
                {t('newProject.libraryEmpty', 'Your library is empty.')}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-grow overflow-y-auto p-6 max-w-4xl mx-auto w-full">
          <ProjectDetailsForm
            title={details.title}
            metadata={initialPattern?.structure?.metadata ?? selected.structure?.metadata}
            patternId={selected.id}
            initialColor={selected.color}
            onChange={setDetails}
            onValidityChange={setDetailsValid}
          />
          <p className="text-xs text-ink-faint mt-2">
            {t('newProject.patternLabel', 'Pattern: {{title}}', { title: selected.title })}
          </p>
          {selected.structure && selected.structure.params.length > 0 && (
            <div className="mt-4">
              <PatternParamsCard
                params={selected.structure.params}
                values={paramValues}
                onChange={setParamValues}
                onValidityChange={setParamsValid}
              />
            </div>
          )}
        </div>
      )}

      <div className="p-6 border-t border-line">
        {selected ? (
          <PrimaryButton
            onClick={handleStart}
            disabled={!detailsValid || !paramsValid}
            className="py-4 rounded-2xl"
          >
            {t('newProject.actions.knit', 'Knit')}
          </PrimaryButton>
        ) : (
          <SoftButton onClick={onCancel} className="text-ink-faint bg-transparent py-4">
            {t('common:actions.cancel', 'Cancel')}
          </SoftButton>
        )}
      </div>
    </div>
  );
};
